import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useRecoilValue, useRecoilState, useSetRecoilState} from "recoil";
import theatreModeState from "../../../state/ui/theatreModeState";
import tabState from "../../../state/ui/activeTabState";
import useFetchChat from "../../../hooks/useLoadChat";
import useLoadPeople from "../../../hooks/useLoadPeople";
import {connectedState, firebaseUidState} from "../../../state/firebaseState";
import useWatchEmojis, {GLOBAL_CLEANUP_EMOJIS, GLOBAL_EMOJIS} from "../../../hooks/useWatchEmojis";
import emojisState, {limitedEmojiState} from "../../../state/emojisState";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Avatar } from "../../../components/Avatar";
import windowDimensionState from "../../../state/ui/windowDimensionState";
import { usePageVisibility } from "../../../hooks/ui/usePageVisibility";
import { useTranslation } from "react-i18next";
import useFetchEmojis from "../../../hooks/useFetchEmojis";
import {sortedPeopleState} from "../../../state/peopleState";
import largeVideoState from "../../../state/ui/largeVideoState";
import boothModeState from "../../../state/ui/boothModeState";


function PeopleItem({item, isMe}) {
  const {t} = useTranslation('lounge');
  const nodeRef = useRef();
  const [showItem, setShowItem] = useState(false);

  const emojis = useRecoilValue(emojisState);

  const filteredEmojis = emojis.filter(i=> i.userId === item.id);
  const emojisToShow = filteredEmojis[filteredEmojis.length - 1];


  useEffect(()=>{
    setShowItem(true);
  },[])

  return (
    <CSSTransition
      in={showItem}
      timeout={4000}
      classNames="show-people"
      unmountOnExit
      onEnter={() => setShowItem(true)}
      onExited={() => setShowItem(false)}
      nodeRef={nodeRef}
    >
      <div className={`relative w-16 mx-3 mb-4 hint--top hint--bounce`} aria-label={item.name} ref={nodeRef}>
          <Avatar
            initial={item.avatar}
            character={item.name && item.name.split(" ").map((n)=>n[0]).slice(0,1).join("")}
            containerStyle={`${isMe ? 'border-2 border-white':''}`}
            user={item}
          />
          {
            isMe ?
              <div className="absolute bottom-5 right-0 left-0">
                <div className="bg-white rounded-2xl m-auto max-w-min px-2">
                  <p className="text-black text-2xs font-bold">{t('you')}</p>
                </div>
              </div>
            :
            null
          }
          <div className="w-full">
            <p className="text-white text-center ellipsis text-xs">{item.name}</p>
          </div>

          {emojisToShow && <EmojiReaction item={emojisToShow} key={item.message}/>}

          {/* <div className="emoji-reaction">
            <p>😄</p>
          </div> */}
      </div>
    </CSSTransition>
  )
};

const EmojiReaction = ({item}) => {
  const nodeRef = useRef();
  const [showItem, setShowItem] = useState(false);

  useEffect(()=>{
    setShowItem(true);
  },[])

  return (
    <CSSTransition
      in={showItem}
      timeout={5000}
      classNames="show-reaction"
      // unmountOnExit
      onEnter={() => setShowItem(true)}
      onExited={() => setShowItem(false)}
      nodeRef={nodeRef}
    >
      <div className="emoji-reaction" ref={nodeRef}>
        <p>{item.message}</p>
      </div>
    </CSSTransition>
  )
}

const EmojiItem = ({item, onItemEnd}) => {
  const ref = useRef();

  const [offset] = useState(Math.random() * 100);
  const [delay] = useState(Math.random() * 3000)

  useLayoutEffect(() => {
    if(!item.id) {
      return;
    }

    const animationEnd = () => {
      onItemEnd();
    };

    ref.current.addEventListener('animationend', animationEnd);
    ref.current.addEventListener('animationcancel', animationEnd);

    return () => {
      ref.current.removeEventListener('animationend', animationEnd);
      ref.current.removeEventListener('animationcancel', animationEnd);
    }
  }, [item?.id]);

  // console.log(item.message)
  const emojiSize = 1 * (item.count);
  // fontSize: `${emojiSize > 10 ? 10 : emojiSize}rem`,

  return (
    <p ref={ref} className="emoji-item" style={{
      left: `${offset}%`,
      animationDelay: item?.isMe ? '0ms' : `${delay}ms`,
    }}>
      <span
        className="transition-all duration-200 ease-in inline-block"
        style={{
          transform: `scale(${emojiSize > 3 ? (emojiSize >= 15 ? 5 : emojiSize/3):1})`
      }}>{item.message}</span>
    </p>
  );
};

const EmojiItems = ({dataRef}) => {
  const setEmojis = useSetRecoilState(emojisState);
  const emojisToShow = useRecoilValue(emojisState);

  return (
    emojisToShow.map(item => <EmojiItem key={item.id} item={item} onItemEnd={() => {
      dataRef.current.removeQueue.push(item);
    }} />)
  )
};

const PeopleItems = () => {
  const theatreMode = useRecoilValue(theatreModeState);
  const connected = useRecoilValue(connectedState);
  const {isLoading} = useLoadPeople();
  const peopleToShow = useRecoilValue(sortedPeopleState);
  const firebaseUid = useRecoilValue(firebaseUidState);

  return (
    <div className={`people-container`}>
      <div className={`flex flex-row flex-wrap justify-center pb-20 ${theatreMode ? 'md:px-8' : ''}`}>
        {!connected || isLoading ? (
          <i className="text-white opacity-50 fas fa-2x fa-circle-notch fa-spin fa-fw" />
        ) : peopleToShow?.map((item, index) => <PeopleItem item={item} key={"p_" + item.id} isMe={item.id === firebaseUid}/>)}
      </div>
    </div>
  )
}

export default function People() {
  const emojisArr = useFetchEmojis();
  const activeTab = useRecoilValue(tabState);

  const windowDimensions = useRecoilValue(windowDimensionState);
  const isVisible = usePageVisibility();
  const {dataRef,send} = useWatchEmojis((windowDimensions.width > 768 || activeTab === 1) && isVisible);
  const largeVideo = useRecoilValue(largeVideoState);
  const boothMode = useRecoilValue(boothModeState);

  return (
    <div className={`people tab-view overflow-hidden ${activeTab !== 1 ? 'hidden user-select-none' : ''} ${largeVideo ? 'people-large':''}`}>

        <PeopleItems />

        <div className="bg-gradient-shadow-b"/>

        <EmojiItems dataRef={dataRef} />

        <div className="relative">
          <div className="emoji-bar bg-panel tab-view-inset-top">
            {/* 🎉 👍 👻 */}
            {emojisArr.map((emoji, index) => (
              <button
                key={'emoji-'+index}
                className="text-3xl flex-1 rounded-lg overflow-hidden hover:bg-white hover:bg-opacity-5 active:scale-75 transition-transform transform ease-in"
                onClick={() => {
                  send(emoji)
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
        
    </div>
  )
}
